import type { ReactNode } from 'react';
import { Zap } from 'lucide-react';
import type { OrganizationWithPeople } from '../../types';
import { CustomerStatusBadge } from './CustomerStatusBadge';
import {
  recencyDisplay,
  formatDollars,
  currentSchoolYearKey,
  type RecencyTone,
} from '../../lib/snapshotMetrics';

interface OrgSnapshotProps {
  organization: OrganizationWithPeople;
}

const TONE_DOT: Record<RecencyTone, string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-rose-500',
};

/** Small status dot. `testId` is consumed by tests to assert tone. */
function Dot({ tone, testId }: { tone: RecencyTone; testId?: string }) {
  return (
    <span
      data-testid={testId}
      data-tone={tone}
      className={`h-1.5 w-1.5 shrink-0 rounded-full ${TONE_DOT[tone]}`}
    />
  );
}

/** A group tile: label, one hero value on a shared baseline, then tidy rows. */
function Group({
  label,
  hero,
  heroCaption,
  children,
}: {
  label: string;
  hero: ReactNode;
  heroCaption: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 px-4 py-4 sm:px-5 sm:py-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">
        {label}
      </div>
      <div className="mt-2.5 text-[1.6rem] font-semibold leading-none tracking-tight tabular-nums text-gray-900 dark:text-gray-50">
        {hero}
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
        {heroCaption}
      </div>
      {children && <div className="mt-3 space-y-1 text-[13px] leading-tight">{children}</div>}
    </div>
  );
}

/** A tidy left-packed key/value row (no edge-to-edge stretch). */
function Row({ tone, testId, label, value }: {
  tone?: RecencyTone;
  testId?: string;
  label?: string;
  value: ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      {tone && <Dot tone={tone} testId={testId} />}
      {label && <span className="text-gray-400 dark:text-gray-500">{label}</span>}
      <span className="min-w-0 truncate font-medium text-gray-700 dark:text-gray-200">{value}</span>
    </div>
  );
}

export const OrgSnapshot = ({ organization }: OrgSnapshotProps) => {
  const owner = organization.assignedRep?.displayName ?? 'Unassigned';
  const location = [organization.city, organization.state].filter(Boolean).join(', ');

  // Engagement
  const visit = recencyDisplay(organization.lastVisitAt, 'visit');
  const touch = recencyDisplay(organization.lastTouchAt, 'touch');

  // School
  const enrollment = organization.ncesEnrollment
    ? organization.ncesEnrollment.toLocaleString()
    : '—';
  const gradeRange =
    organization.ncesGradeLow && organization.ncesGradeHigh
      ? `${organization.ncesGradeLow}–${organization.ncesGradeHigh}`
      : null;
  const levelLine = [organization.ncesLevel, gradeRange].filter(Boolean).join(' · ') || null;
  const hasSchoolDetail = Boolean(levelLine || organization.district || organization.conference);

  // Fundraising
  const hasFundraisers = organization.lifetimeFundraiserCount > 0;
  const lifetime = formatDollars(organization.lifetimeTotalRaisedCents);
  const rollups = organization.orgDonationRollups ?? [];
  const lifeRollup = rollups.find((r) => r.periodType === 'LIFETIME');
  const currentKey = currentSchoolYearKey();
  const syRollup = rollups.find((r) => r.periodType === 'SCHOOL_YEAR' && r.periodKey === currentKey);
  const currentSy = formatDollars(syRollup ? syRollup.raisedCents : '0');
  const donorCount = lifeRollup?.donorCount ?? 0;
  const avgGift = lifeRollup && lifeRollup.donationCount > 0 ? formatDollars(lifeRollup.avgGiftCents) : null;
  const lastFr = organization.lastFundraiserLaunchedAt
    ? new Date(organization.lastFundraiserLaunchedAt).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', timeZone: 'UTC',
      })
    : null;
  const lastFrAmt = organization.lastFundraiserAmountCents
    ? formatDollars(organization.lastFundraiserAmountCents)
    : null;

  return (
    <div className="card overflow-hidden">
      {/* Header — title left; account status + location right */}
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700/70 sm:px-5">
        <Zap className="h-4 w-4 shrink-0 text-blast-600 dark:text-blast-400" />
        <h2 className="text-sm font-semibold tracking-tight text-gray-900 dark:text-gray-100">Snapshot</h2>
        <div className="ml-auto flex min-w-0 items-center gap-2.5">
          <CustomerStatusBadge status={organization.customerStatus} size="sm" />
          {location && (
            <span className="hidden min-w-0 truncate text-xs text-gray-400 dark:text-gray-500 sm:block">
              {location}
            </span>
          )}
        </div>
      </div>

      {/* Three stat tiles — stacked on mobile, 1×3 strip on desktop. gap-px reveals
          the divider color behind, giving crisp separators in either layout. */}
      <div className="grid grid-cols-1 gap-px bg-gray-100 dark:bg-gray-700/70 sm:grid-cols-3">
        {/* Engagement — last touch is the hero; last visit + owner below */}
        <Group
          label="Engagement"
          hero={<span className={touch.tone === 'red' ? 'text-rose-600 dark:text-rose-400' : ''}>{touch.text}</span>}
          heroCaption={<><Dot tone={touch.tone} testId="touch-dot" /><span>last touch</span></>}
        >
          <Row tone={visit.tone} testId="visit-dot" label="Visit" value={visit.text} />
          <Row label="Owner" value={owner} />
        </Group>

        {/* School — enrollment hero; level / district / conference below. School-only:
            clubs/bands have no NCES profile, so the tile is omitted for them (CRM-71). */}
        {organization.type === 'SCHOOL' && (
          <Group label="School" hero={enrollment} heroCaption={<span>enrollment</span>}>
            {hasSchoolDetail ? (
              <>
                {levelLine && <div className="truncate text-gray-600 dark:text-gray-300">{levelLine}</div>}
                {organization.district && <div className="truncate text-gray-600 dark:text-gray-300">{organization.district}</div>}
                {organization.conference && <div className="truncate text-gray-600 dark:text-gray-300">{organization.conference}</div>}
              </>
            ) : (
              <div className="text-gray-400 dark:text-gray-500">No school profile</div>
            )}
          </Group>
        )}

        {/* Fundraising — lifetime hero; current-SY + avg gift + last raiser below */}
        {hasFundraisers ? (
          <Group
            label="Fundraising"
            hero={lifetime}
            heroCaption={<span>lifetime · {organization.lifetimeFundraiserCount} raisers · {donorCount.toLocaleString()} donors</span>}
          >
            <div className="flex items-center gap-1.5">
              <span className="text-gray-400 dark:text-gray-500">This SY</span>
              <span className="font-medium tabular-nums text-gray-700 dark:text-gray-200">{currentSy}</span>
            </div>
            {avgGift && <Row label="Avg gift" value={avgGift} />}
            {lastFr && (
              <Row label="Last" value={`${lastFr}${lastFrAmt ? ` · ${lastFrAmt}` : ''}`} />
            )}
          </Group>
        ) : (
          <Group label="Fundraising" hero={<span className="text-gray-300 dark:text-gray-600">—</span>} heroCaption={<span>No fundraisers yet</span>} />
        )}
      </div>
    </div>
  );
};
